const EXTERNAL_IMAGE_PATTERN = /^(?:[a-z][a-z\d+.-]*:|\/\/|#)/i;

const normalizePath = value => {
  const parts = String(value || "")
    .replace(/\\/g, "/")
    .split("/");
  const normalized = [];
  for (const part of parts) {
    if (!part || part === ".") continue;
    if (
      part === ".." &&
      normalized.length > 0 &&
      normalized[normalized.length - 1] !== ".."
    ) {
      normalized.pop();
      continue;
    }
    normalized.push(part);
  }
  return normalized.join("/");
};

const decodePath = value => {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

const relativePath = (fromDirectory, targetPath) => {
  const from = normalizePath(fromDirectory).split("/").filter(Boolean);
  const target = normalizePath(targetPath).split("/").filter(Boolean);
  let common = 0;
  while (
    common < from.length &&
    common < target.length &&
    from[common] === target[common]
  ) {
    common += 1;
  }
  return [
    ...Array(from.length - common).fill(".."),
    ...target.slice(common),
  ].join("/");
};

export const assetPaths = (file, markdownFile) => {
  const filePath = normalizePath(file.webkitRelativePath || file.name);
  const markdownPath = normalizePath(
    markdownFile && (markdownFile.webkitRelativePath || markdownFile.name)
  );
  const paths = new Set([filePath, normalizePath(file.name)]);
  if (markdownPath && filePath) {
    paths.add(
      relativePath(markdownPath.split("/").slice(0, -1).join("/"), filePath)
    );
  }
  return Array.from(paths).filter(Boolean);
};

export const createImageAssetMap = (files, markdownFile) => {
  const assets = {};
  for (const file of files || []) {
    const url = URL.createObjectURL(file);
    for (const path of assetPaths(file, markdownFile)) {
      assets[path] = url;
    }
  }
  return assets;
};

export const releaseImageAssetMap = assets => {
  for (const url of new Set(Object.values(assets || {}))) {
    URL.revokeObjectURL(url);
  }
};

const findAsset = (source, assets) => {
  const decoded = decodePath(source);
  const candidates = [source, decoded, normalizePath(source), normalizePath(decoded)];
  for (const candidate of candidates) {
    if (assets[candidate]) return assets[candidate];
  }
  const basename = normalizePath(decoded).split("/").pop();
  if (basename && assets[basename]) return assets[basename];
  return undefined;
};

export const resolveImageSource = (source, assets = {}) => {
  if (!source || EXTERNAL_IMAGE_PATTERN.test(source)) return source;
  return findAsset(source, assets) || source;
};

const MARKDOWN_IMAGE_PATTERN = /!\[[^\]]*\]\(\s*(<[^>\n]+>|[^)\s]+)([^\n)]*)\)/g;
const HTML_IMAGE_PATTERN = /<img\b[^>]*\bsrc\s*=\s*["']([^"']+)["']/gi;

export const extractLocalImagePaths = markdown => {
  const paths = new Set();
  const consider = source => {
    if (source && !EXTERNAL_IMAGE_PATTERN.test(source)) paths.add(source);
  };
  let match;
  while ((match = MARKDOWN_IMAGE_PATTERN.exec(markdown || ""))) {
    consider(match[1].startsWith("<") ? match[1].slice(1, -1) : match[1]);
  }
  while ((match = HTML_IMAGE_PATTERN.exec(markdown || ""))) {
    consider(match[1]);
  }
  return Array.from(paths);
};

export const resolveMarkdownImages = (markdown, assets = {}) => {
  const resolvedMarkdown = String(markdown || "").replace(
    /(!\[[^\]]*\]\(\s*)(<[^>\n]+>|[^)\s]+)([^\n)]*\))/g,
    (match, prefix, source, suffix) => {
      const unwrappedSource = source.startsWith("<")
        ? source.slice(1, -1)
        : source;
      const resolvedSource = resolveImageSource(unwrappedSource, assets);
      const outputSource = source.startsWith("<")
        ? `<${resolvedSource}>`
        : resolvedSource;
      return `${prefix}${outputSource}${suffix}`;
    }
  );

  return resolvedMarkdown.replace(
    /(<img\b[^>]*\bsrc\s*=\s*["'])([^"']+)(["'][^>]*>)/gi,
    (match, prefix, source, suffix) =>
      `${prefix}${resolveImageSource(source, assets)}${suffix}`
  );
};
